import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { Clipboard } from '@angular/cdk/clipboard';
import { ColorService } from '../../service/color/color.service';

// A purpose-built hero card for the exact-match converter pages. It shares the
// color widget's outer footprint and left column (swatch + editable hex) so the
// pages read as one system, but the right side is its own converter output: the
// direction, the big result value + copy, and the three channels as bars.
// Supports both converter families: HEX<->RGB and HEX<->HSL; the family is
// derived from the labels, so each page just passes its own from/to.
// (Deliberately NOT app-color-widget: that is a generic colour identity list;
// this is a from->to converter, so its right half is designed for that.)
@Component( {
  selector: 'app-converter-hero',
  templateUrl: './converter-hero.component.html',
  styleUrls: [ './converter-hero.component.scss' ],
} )
export class ConverterHeroComponent implements OnChanges {
  @Input() hex = '';
  @Input() fromLabel = 'HEX';
  @Input() toLabel = 'RGB';
  @Output() colorChange = new EventEmitter<string>();

  current = '';
  hexText = '';
  // The reverse direction's editable channel fields (RGB / HSL / CMYK values);
  // three entries for every family except CMYK's four.
  channelTexts: string[] = [ '', '', '' ];
  rgbString = '';
  hslString = '';
  hsvString = '';
  hsbString = '';
  hwbString = '';
  cmykString = '';
  oklchString = '';
  oklabString = '';
  labString = '';
  lchString = '';
  p3String = '';
  xyzString = '';
  yxyString = '';
  hunterString = '';
  luvString = '';
  hexDisplay = '';
  copiedKey = '';
  private copiedTimer: any;

  // X -> HEX runs the same card the other way round: the editable field under
  // the swatch is the three channels and the big result is the hex code.
  get reverse(): boolean {
    return this.fromLabel.toUpperCase() !== 'HEX';
  }

  // Which converter family the card serves; drives the channel bars, the
  // editable reverse fields and the big result value.
  get family(): 'rgb' | 'hsl' | 'hsv' | 'hsb' | 'hwb' | 'cmyk' | 'oklch' | 'oklab' | 'lab' | 'lch' | 'p3' | 'xyz' | 'yxy' | 'hunter' | 'luv' {
    const labels = ( this.fromLabel + this.toLabel ).toUpperCase();
    if ( labels.includes( 'DISPLAY P3' ) ) { return 'p3'; }
    if ( labels.includes( 'HUNTER' ) ) { return 'hunter'; }
    if ( labels.includes( 'CIELUV' ) ) { return 'luv'; }
    if ( labels.includes( 'YXY' ) ) { return 'yxy'; }
    if ( labels.includes( 'XYZ' ) ) { return 'xyz'; }
    if ( labels.includes( 'OKLCH' ) ) { return 'oklch'; }
    if ( labels.includes( 'OKLAB' ) ) { return 'oklab'; }
    if ( labels.includes( 'CIELCH' ) ) { return 'lch'; }
    if ( labels.includes( 'CIELAB' ) ) { return 'lab'; }
    if ( labels.includes( 'HSL' ) ) { return 'hsl'; }
    if ( labels.includes( 'HSV' ) ) { return 'hsv'; }
    if ( labels.includes( 'HSB' ) ) { return 'hsb'; }
    if ( labels.includes( 'HWB' ) ) { return 'hwb'; }
    if ( labels.includes( 'CMYK' ) ) { return 'cmyk'; }
    return 'rgb';
  }

  get outString(): string {
    return this.reverse ? this.hexDisplay : this.familyString;
  }

  /** the value converted, written out, before the arrow on the result line ("yxy(...) → #ABCDEF", owner
   *  2026-10-12) */
  get inString(): string {
    return this.reverse ? this.familyString : this.hexDisplay;
  }

  /** the colour in the page's other notation (rgb(...), hsl(...), yxy(...) ...) */
  private get familyString(): string {
    if ( this.family === 'hsl' ) { return this.hslString; }
    if ( this.family === 'hsv' ) { return this.hsvString; }
    if ( this.family === 'hsb' ) { return this.hsbString; }
    if ( this.family === 'hwb' ) { return this.hwbString; }
    if ( this.family === 'cmyk' ) { return this.cmykString; }
    if ( this.family === 'oklch' ) { return this.oklchString; }
    if ( this.family === 'oklab' ) { return this.oklabString; }
    if ( this.family === 'lab' ) { return this.labString; }
    if ( this.family === 'lch' ) { return this.lchString; }
    if ( this.family === 'p3' ) { return this.p3String; }
    if ( this.family === 'xyz' ) { return this.xyzString; }
    if ( this.family === 'yxy' ) { return this.yxyString; }
    if ( this.family === 'hunter' ) { return this.hunterString; }
    if ( this.family === 'luv' ) { return this.luvString; }
    return this.rgbString;
  }

  get channelLabels(): string[] {
    if ( this.family === 'hsl' ) { return [ 'Hue', 'Saturation', 'Lightness' ]; }
    if ( this.family === 'hsv' ) { return [ 'Hue', 'Saturation', 'Value' ]; }
    if ( this.family === 'hsb' ) { return [ 'Hue', 'Saturation', 'Brightness' ]; }
    if ( this.family === 'hwb' ) { return [ 'Hue', 'Whiteness', 'Blackness' ]; }
    if ( this.family === 'cmyk' ) { return [ 'Cyan', 'Magenta', 'Yellow', 'Key' ]; }
    if ( this.family === 'oklch' || this.family === 'lch' ) { return [ 'Lightness', 'Chroma', 'Hue' ]; }
    if ( this.family === 'oklab' || this.family === 'lab' ) { return [ 'Lightness', 'a', 'b' ]; }
    if ( this.family === 'xyz' ) { return [ 'X', 'Y', 'Z' ]; }
    if ( this.family === 'yxy' ) { return [ 'Y', 'x', 'y' ]; }
    if ( this.family === 'hunter' ) { return [ 'L', 'a', 'b' ]; }
    if ( this.family === 'luv' ) { return [ 'L', 'u', 'v' ]; }
    return [ 'Red', 'Green', 'Blue' ];
  }

  /** Families whose reverse fields are all free decimals (P3 0..1 channels,
   *  XYZ / Yxy / Hunter Lab / CIELUV values) rather than 3-digit integers. */
  get decimalFamily(): boolean {
    const f = this.family;
    return f === 'p3' || f === 'xyz' || f === 'yxy' || f === 'hunter' || f === 'luv';
  }

  private get channelMaxes(): number[] {
    if ( this.family === 'rgb' ) { return [ 255, 255, 255 ]; }
    if ( this.family === 'cmyk' ) { return [ 100, 100, 100, 100 ]; }
    // OKLCH reverse fields are integers: L as a percent, chroma x1000 (0.152 ->
    // 152), hue in degrees - matching the /tools/oklch-to-hex/63-152-264 slug.
    if ( this.family === 'oklch' ) { return [ 100, 500, 360 ]; }
    // CIELCH: L percent, chroma 0..~150, hue in degrees.
    if ( this.family === 'lch' ) { return [ 100, 200, 360 ]; }
    // OKLAB / CIELAB are handled separately (signed decimal a/b); cap bounds L only.
    if ( this.family === 'oklab' || this.family === 'lab' ) { return [ 100, 1, 1 ]; }
    return [ 360, 100, 100 ];
  }

  // The three channels, each with its value, tint and bar fill against its max.
  channels: { label: string; value: number; max: number; display: string; tint: string; track: string }[] = [];

  constructor( private colorService: ColorService, private clipboard: Clipboard ) {}

  ngOnChanges( changes: SimpleChanges ): void {
    if ( changes[ 'hex' ] && this.hex && this.hex !== this.current ) {
      this.setHex( this.hex, false, true );
    }
  }

  onColorChange( value: string ) {
    this.setHex( ( value || '' ).toLowerCase(), true, true );
  }

  onHexTextChange( value: string ) {
    const raw = ( value || '' ).replace( /[^0-9a-fA-F]/g, '' ).toLowerCase().slice( 0, 6 );
    this.hexText = raw;
    if ( raw.length !== 6 || !this.colorService.isValidHexCode( raw ) ) { return; }
    this.setHex( '#' + raw, true, false );
  }

  onHexEnter( event: Event ) {
    ( event.target as HTMLInputElement ).blur();
  }

  onHexFocus( event: Event ) {
    const input = event.target as HTMLInputElement;
    setTimeout( () => {
      const end = input.value.length;
      input.setSelectionRange( end, end );
    } );
  }

  onHexKeydown( event: KeyboardEvent ) {
    if ( event.ctrlKey || event.metaKey ) { return; }
    if ( event.key && event.key.length === 1 && !/[0-9a-fA-F]/.test( event.key ) ) {
      event.preventDefault();
    }
  }

  onChannelTextChange( index: number, value: string ) {
    if ( this.decimalFamily ) {
      // Every field is a decimal; Hunter Lab's a/b and CIELUV's u/v are signed. Bounds per family.
      this.channelTexts[ index ] = ( value || '' ).replace( /[^0-9.\-]/g, '' ).slice( 0, 8 );
      const v = this.channelTexts.map( t => parseFloat( t ) );
      if ( v.some( n => isNaN( n ) ) ) { return; }
      const f = this.family;
      let hex: string | null = null;
      if ( f === 'p3' && v.every( n => n >= 0 && n <= 1 ) ) {
        hex = this.colorService.displayP3ToHex( v[ 0 ], v[ 1 ], v[ 2 ] );
      } else if ( f === 'xyz' && v[ 0 ] >= 0 && v[ 0 ] <= 110 && v[ 1 ] >= 0 && v[ 1 ] <= 100 && v[ 2 ] >= 0 && v[ 2 ] <= 110 ) {
        hex = this.colorService.xyzToHex( v[ 0 ] / 100, v[ 1 ] / 100, v[ 2 ] / 100 );
      } else if ( f === 'yxy' && v[ 0 ] >= 0 && v[ 0 ] <= 100 && v[ 1 ] >= 0 && v[ 1 ] <= 1 && v[ 2 ] >= 0 && v[ 2 ] <= 1 ) {
        hex = this.colorService.yxyToHex( v[ 0 ], v[ 1 ], v[ 2 ] );
      } else if ( f === 'hunter' && v[ 0 ] >= 0 && v[ 0 ] <= 100 && Math.abs( v[ 1 ] ) <= 200 && Math.abs( v[ 2 ] ) <= 200 ) {
        hex = this.colorService.hunterLabToHex( v[ 0 ], v[ 1 ], v[ 2 ] );
      } else if ( f === 'luv' && v[ 0 ] >= 0 && v[ 0 ] <= 100 && Math.abs( v[ 1 ] ) <= 200 && Math.abs( v[ 2 ] ) <= 200 ) {
        hex = this.colorService.luvToHex( v[ 0 ], v[ 1 ], v[ 2 ] );
      }
      if ( hex ) { this.setHex( '#' + hex, true, false ); }
      return;
    }
    if ( this.family === 'oklab' || this.family === 'lab' ) {
      // L is an integer percent; a and b are signed decimals (green->red, blue->yellow),
      // so those two fields accept a minus sign and a decimal point. CIELAB a/b run
      // wider (~±128) than OKLab (~±0.4), so allow a couple more characters.
      this.channelTexts[ index ] = index === 0
        ? ( value || '' ).replace( /[^0-9]/g, '' ).slice( 0, 3 )
        : ( value || '' ).replace( /[^0-9.\-]/g, '' ).slice( 0, 7 );
      const L = parseFloat( this.channelTexts[ 0 ] );
      const a = parseFloat( this.channelTexts[ 1 ] );
      const b = parseFloat( this.channelTexts[ 2 ] );
      if ( isNaN( L ) || isNaN( a ) || isNaN( b ) || L < 0 || L > 100 ) { return; }
      const hex = this.family === 'lab'
        ? this.colorService.labToHex( L, a, b )
        : this.colorService.oklabToHex( L / 100, a, b );
      this.setHex( '#' + hex, true, false );
      return;
    }
    const max = this.channelMaxes[ index ];
    let raw = ( value || '' ).replace( /[^0-9]/g, '' ).slice( 0, 3 );
    if ( raw.length && parseInt( raw, 10 ) > max ) { raw = String( max ); }
    this.channelTexts[ index ] = raw;
    const nums = this.channelTexts.map( t => ( t === '' ? NaN : parseInt( t, 10 ) ) );
    if ( nums.some( ( n, i ) => isNaN( n ) || n < 0 || n > this.channelMaxes[ i ] ) ) { return; }
    const hex = this.family === 'hsl'
      ? '#' + this.colorService.hslToHex( nums[ 0 ], nums[ 1 ], nums[ 2 ] )
      : this.family === 'hsv' || this.family === 'hsb'
        ? '#' + this.colorService.hsvToHex( nums[ 0 ], nums[ 1 ], nums[ 2 ] )
        : this.family === 'hwb'
          ? '#' + this.colorService.hwbToHex( nums[ 0 ], nums[ 1 ], nums[ 2 ] )
          : this.family === 'cmyk'
            ? '#' + this.colorService.cmykToHex( nums[ 0 ], nums[ 1 ], nums[ 2 ], nums[ 3 ] )
            : this.family === 'oklch'
              ? '#' + this.colorService.oklchToHex( nums[ 0 ] / 100, nums[ 1 ] / 1000, nums[ 2 ] )
              : this.family === 'lch'
                ? '#' + this.colorService.lchToHex( nums[ 0 ], nums[ 1 ], nums[ 2 ] )
                : this.colorService.rgbToHex( nums[ 0 ], nums[ 1 ], nums[ 2 ] );
    this.setHex( hex, true, false );
  }

  onChannelKeydown( event: KeyboardEvent ) {
    if ( event.ctrlKey || event.metaKey ) { return; }
    // OKLAB's a/b fields accept a minus sign and a decimal point; every other
    // family is digits only.
    const allowed = ( this.family === 'oklab' || this.family === 'lab' || this.decimalFamily ) ? /[0-9.\-]/ : /[0-9]/;
    if ( event.key && event.key.length === 1 && !allowed.test( event.key ) ) {
      event.preventDefault();
    }
  }

  onChannelBlur() {
    this.syncChannelTexts();
  }

  copy( text: string, key: string ) {
    this.clipboard.copy( text );
    this.copiedKey = key;
    if ( this.copiedTimer ) { clearTimeout( this.copiedTimer ); }
    this.copiedTimer = setTimeout( () => ( this.copiedKey = '' ), 1200 );
  }

  private setHex( hex: string, emit: boolean, syncText: boolean ) {
    this.current = hex;
    if ( syncText ) {
      this.hexText = hex.substring( 1 ).toLowerCase();
      this.syncChannelTexts();
    }
    const rgb = this.colorService.hexToRgb( hex );
    const r = rgb.items[ 0 ]?.value ?? 0;
    const g = rgb.items[ 1 ]?.value ?? 0;
    const b = rgb.items[ 2 ]?.value ?? 0;
    const hsl = this.colorService.hexToHslInts( hex );
    const hsv = this.colorService.hexToHsv( hex );
    const hwb = this.colorService.hexToHwb( hex );
    const cmyk = this.colorService.hexToCmyk( hex ).items.map( ( i: { value: number } ) => i.value );
    const oklch = this.colorService.hexToOklch( hex );
    const okL = Math.round( oklch[ 0 ] * 100 );
    const okC = Math.round( oklch[ 1 ] * 1000 ) / 1000;
    const okH = Math.round( oklch[ 2 ] );
    const oklab = this.colorService.hexToOklab( hex );
    const oaL = Math.round( oklab[ 0 ] * 100 );
    const oaA = Math.round( oklab[ 1 ] * 1000 ) / 1000;
    const oaB = Math.round( oklab[ 2 ] * 1000 ) / 1000;
    const lab = this.colorService.hexToLab( hex );
    const laL = Math.round( lab[ 0 ] );
    const laA = Math.round( lab[ 1 ] * 100 ) / 100;
    const laB = Math.round( lab[ 2 ] * 100 ) / 100;
    const lch = this.colorService.hexToLch( hex );
    const lcL = Math.round( lch[ 0 ] );
    const lcC = Math.round( lch[ 1 ] * 100 ) / 100;
    const lcH = Math.round( lch[ 2 ] );
    this.rgbString = `rgb(${ r }, ${ g }, ${ b })`;
    this.hslString = `hsl(${ hsl.h }, ${ hsl.s }%, ${ hsl.l }%)`;
    this.hsvString = `hsv(${ hsv[ 0 ] }, ${ hsv[ 1 ] }%, ${ hsv[ 2 ] }%)`;
    this.hsbString = `hsb(${ hsv[ 0 ] }, ${ hsv[ 1 ] }%, ${ hsv[ 2 ] }%)`;
    // hwb() and oklch() are CSS Color 4 syntax: space-separated, no comma form.
    this.hwbString = `hwb(${ hwb[ 0 ] } ${ hwb[ 1 ] }% ${ hwb[ 2 ] }%)`;
    this.cmykString = `cmyk(${ cmyk[ 0 ] }%, ${ cmyk[ 1 ] }%, ${ cmyk[ 2 ] }%, ${ cmyk[ 3 ] }%)`;
    this.oklchString = `oklch(${ okL }% ${ okC } ${ okH })`;
    this.oklabString = `oklab(${ oaL }% ${ oaA } ${ oaB })`;
    this.labString = `lab(${ laL }% ${ laA } ${ laB })`;
    this.lchString = `lch(${ lcL }% ${ lcC } ${ lcH })`;
    const p3 = this.colorService.hexToDisplayP3( hex );
    const xyz = this.colorService.hexToXyz( hex ).map( ( n: number ) => Math.round( n * 10000 ) / 100 );
    const yxy = this.colorService.hexToYxy( hex );
    const hunter = this.colorService.hexToHunterLab( hex );
    const luv = this.colorService.hexToLuv( hex );
    this.p3String = `color(display-p3 ${ p3[ 0 ] } ${ p3[ 1 ] } ${ p3[ 2 ] })`;
    this.xyzString = `xyz(${ xyz[ 0 ] }, ${ xyz[ 1 ] }, ${ xyz[ 2 ] })`;
    this.yxyString = `yxy(${ yxy[ 0 ] }, ${ yxy[ 1 ] }, ${ yxy[ 2 ] })`;
    this.hunterString = `hunter-lab(${ hunter[ 0 ] }, ${ hunter[ 1 ] }, ${ hunter[ 2 ] })`;
    // CSS has no luv(): written comma-separated like hunter-lab().
    this.luvString = `luv(${ luv[ 0 ] }, ${ luv[ 1 ] }, ${ luv[ 2 ] })`;
    this.hexDisplay = '#' + hex.substring( 1 ).toUpperCase();
    // HSL / HSV bar colours are derived from the actual color, mirroring the
    // color page's Color Channels section: H shows the hue (toned down so bright
    // hues don't wash out), S the hue at its real saturation, L the colour
    // itself, V the hue ramped from black (l = v / 2).
    // On the reverse pages (X -> HEX) the right side is the hex result, so the
    // bars show its RGB reading; the family bars would just repeat the input.
    if ( this.family === 'hsl' && !this.reverse ) {
      this.channels = [
        { label: 'Hue', value: hsl.h, max: 360, display: hsl.h + '°', tint: `hsl(${ hsl.h }, 70%, 45%)`, track: `hsla(${ hsl.h }, 70%, 45%, 0.16)` },
        { label: 'Sat', value: hsl.s, max: 100, display: hsl.s + '%', tint: `hsl(${ hsl.h }, ${ hsl.s }%, 50%)`, track: `hsla(${ hsl.h }, ${ hsl.s }%, 50%, 0.16)` },
        { label: 'Light', value: hsl.l, max: 100, display: hsl.l + '%', tint: `hsl(${ hsl.h }, ${ hsl.s }%, ${ hsl.l }%)`, track: `hsla(${ hsl.h }, ${ hsl.s }%, ${ hsl.l }%, 0.16)` },
      ];
    } else if ( ( this.family === 'hsv' || this.family === 'hsb' ) && !this.reverse ) {
      const vHalf = Math.round( hsv[ 2 ] / 2 );
      this.channels = [
        { label: 'Hue', value: hsv[ 0 ], max: 360, display: hsv[ 0 ] + '°', tint: `hsl(${ hsv[ 0 ] }, 70%, 45%)`, track: `hsla(${ hsv[ 0 ] }, 70%, 45%, 0.16)` },
        { label: 'Sat', value: hsv[ 1 ], max: 100, display: hsv[ 1 ] + '%', tint: `hsl(${ hsv[ 0 ] }, ${ hsv[ 1 ] }%, 50%)`, track: `hsla(${ hsv[ 0 ] }, ${ hsv[ 1 ] }%, 50%, 0.16)` },
        { label: this.family === 'hsb' ? 'Bright' : 'Value', value: hsv[ 2 ], max: 100, display: hsv[ 2 ] + '%', tint: `hsl(${ hsv[ 0 ] }, ${ hsv[ 1 ] }%, ${ vHalf }%)`, track: `hsla(${ hsv[ 0 ] }, ${ hsv[ 1 ] }%, ${ vHalf }%, 0.16)` },
      ];
    } else if ( this.family === 'hwb' && !this.reverse ) {
      // W bar whitens the hue as whiteness grows, B bar blackens it: both end
      // on pure white / pure black at 100%, mirroring what the values mean.
      const wTint = `hsl(${ hwb[ 0 ] }, ${ 100 - hwb[ 1 ] }%, ${ 50 + Math.round( hwb[ 1 ] / 2 ) }%)`;
      const bTint = `hsl(${ hwb[ 0 ] }, ${ 100 - hwb[ 2 ] }%, ${ 50 - Math.round( hwb[ 2 ] / 2 ) }%)`;
      this.channels = [
        { label: 'Hue', value: hwb[ 0 ], max: 360, display: hwb[ 0 ] + '°', tint: `hsl(${ hwb[ 0 ] }, 70%, 45%)`, track: `hsla(${ hwb[ 0 ] }, 70%, 45%, 0.16)` },
        { label: 'White', value: hwb[ 1 ], max: 100, display: hwb[ 1 ] + '%', tint: wTint, track: `hsla(${ hwb[ 0 ] }, ${ 100 - hwb[ 1 ] }%, ${ 50 + Math.round( hwb[ 1 ] / 2 ) }%, 0.16)` },
        { label: 'Black', value: hwb[ 2 ], max: 100, display: hwb[ 2 ] + '%', tint: bTint, track: `hsla(${ hwb[ 0 ] }, ${ 100 - hwb[ 2 ] }%, ${ 50 - Math.round( hwb[ 2 ] / 2 ) }%, 0.16)` },
      ];
    } else if ( this.family === 'cmyk' && !this.reverse ) {
      // The four bars wear the classic process ink colors (key = near black).
      this.channels = [
        { label: 'Cyan', value: cmyk[ 0 ], max: 100, display: cmyk[ 0 ] + '%', tint: '#0b9fd8', track: 'rgba(11, 159, 216, 0.16)' },
        { label: 'Magenta', value: cmyk[ 1 ], max: 100, display: cmyk[ 1 ] + '%', tint: '#d1197e', track: 'rgba(209, 25, 126, 0.16)' },
        { label: 'Yellow', value: cmyk[ 2 ], max: 100, display: cmyk[ 2 ] + '%', tint: '#e0b400', track: 'rgba(224, 180, 0, 0.16)' },
        { label: 'Key', value: cmyk[ 3 ], max: 100, display: cmyk[ 3 ] + '%', tint: '#3d3d3d', track: 'rgba(61, 61, 61, 0.16)' },
      ];
    } else if ( this.family === 'oklch' && !this.reverse ) {
      // L is a grey ramp at that lightness, Chroma is the colour itself (how far
      // from grey), Hue reads the colour's hue. Chroma bar scales against ~0.4,
      // the practical sRGB ceiling.
      this.channels = [
        { label: 'Light', value: okL, max: 100, display: okL + '%', tint: `hsl(0, 0%, ${ okL }%)`, track: 'rgba(128, 128, 128, 0.16)' },
        { label: 'Chroma', value: Math.round( oklch[ 1 ] * 1000 ), max: 400, display: String( okC ), tint: this.current, track: `hsla(${ hsl.h }, ${ hsl.s }%, 50%, 0.16)` },
        { label: 'Hue', value: okH, max: 360, display: okH + '°', tint: `hsl(${ hsl.h }, 70%, 45%)`, track: `hsla(${ hsl.h }, 70%, 45%, 0.16)` },
      ];
    } else if ( this.family === 'lch' && !this.reverse ) {
      // CIELCH: grey ramp for L, the colour itself for chroma (0..~150 ceiling), the hue.
      this.channels = [
        { label: 'Light', value: lcL, max: 100, display: lcL + '%', tint: `hsl(0, 0%, ${ lcL }%)`, track: 'rgba(128, 128, 128, 0.16)' },
        { label: 'Chroma', value: lch[ 1 ], max: 150, display: String( lcC ), tint: this.current, track: `hsla(${ hsl.h }, ${ hsl.s }%, 50%, 0.16)` },
        { label: 'Hue', value: lcH, max: 360, display: lcH + '°', tint: `hsl(${ hsl.h }, 70%, 45%)`, track: `hsla(${ hsl.h }, 70%, 45%, 0.16)` },
      ];
    } else if ( this.family === 'p3' && !this.reverse ) {
      this.channels = [
        { label: 'Red', value: p3[ 0 ] * 100, max: 100, display: String( p3[ 0 ] ), tint: '#d6493f', track: 'rgba(214, 73, 63, 0.16)' },
        { label: 'Green', value: p3[ 1 ] * 100, max: 100, display: String( p3[ 1 ] ), tint: '#3aa35e', track: 'rgba(58, 163, 94, 0.16)' },
        { label: 'Blue', value: p3[ 2 ] * 100, max: 100, display: String( p3[ 2 ] ), tint: '#3a74e0', track: 'rgba(58, 116, 224, 0.16)' },
      ];
    } else if ( this.family === 'xyz' && !this.reverse ) {
      this.channels = [
        { label: 'X', value: xyz[ 0 ], max: 110, display: String( xyz[ 0 ] ), tint: '#c2185b', track: 'rgba(194, 24, 91, 0.16)' },
        { label: 'Y', value: xyz[ 1 ], max: 100, display: String( xyz[ 1 ] ), tint: '#3d9f6b', track: 'rgba(61, 159, 107, 0.16)' },
        { label: 'Z', value: xyz[ 2 ], max: 110, display: String( xyz[ 2 ] ), tint: '#3f7fc4', track: 'rgba(63, 127, 196, 0.16)' },
      ];
    } else if ( this.family === 'yxy' && !this.reverse ) {
      this.channels = [
        { label: 'Y', value: yxy[ 0 ], max: 100, display: String( yxy[ 0 ] ), tint: '#3d9f6b', track: 'rgba(61, 159, 107, 0.16)' },
        { label: 'x', value: yxy[ 1 ] * 100, max: 100, display: String( yxy[ 1 ] ), tint: '#c2185b', track: 'rgba(194, 24, 91, 0.16)' },
        { label: 'y', value: yxy[ 2 ] * 100, max: 100, display: String( yxy[ 2 ] ), tint: '#3f7fc4', track: 'rgba(63, 127, 196, 0.16)' },
      ];
    } else if ( this.family === 'hunter' && !this.reverse ) {
      // Hunter a and b are signed; map [-100, 100] onto a 0-100% bar so zero sits mid-track.
      const axis = ( v: number ) => Math.max( 0, Math.min( 100, Math.round( ( v + 100 ) / 200 * 100 ) ) );
      this.channels = [
        { label: 'L', value: hunter[ 0 ], max: 100, display: String( hunter[ 0 ] ), tint: `hsl(0, 0%, ${ Math.round( hunter[ 0 ] ) }%)`, track: 'rgba(128, 128, 128, 0.16)' },
        { label: 'a', value: axis( hunter[ 1 ] ), max: 100, display: String( hunter[ 1 ] ), tint: '#c2410c', track: 'rgba(194, 65, 12, 0.16)' },
        { label: 'b', value: axis( hunter[ 2 ] ), max: 100, display: String( hunter[ 2 ] ), tint: '#7c3aed', track: 'rgba(124, 58, 237, 0.16)' },
      ];
    } else if ( this.family === 'luv' && !this.reverse ) {
      // CIELUV u and v are signed and lopsided inside sRGB (u about -84..175, v about
      // -134..107); map [-180, 180] onto a 0-100% bar so zero sits mid-track.
      const axis = ( v: number ) => Math.max( 0, Math.min( 100, Math.round( ( v + 180 ) / 360 * 100 ) ) );
      this.channels = [
        { label: 'L', value: luv[ 0 ], max: 100, display: String( luv[ 0 ] ), tint: `hsl(0, 0%, ${ Math.round( luv[ 0 ] ) }%)`, track: 'rgba(128, 128, 128, 0.16)' },
        { label: 'u', value: axis( luv[ 1 ] ), max: 100, display: String( luv[ 1 ] ), tint: '#c2410c', track: 'rgba(194, 65, 12, 0.16)' },
        { label: 'v', value: axis( luv[ 2 ] ), max: 100, display: String( luv[ 2 ] ), tint: '#7c3aed', track: 'rgba(124, 58, 237, 0.16)' },
      ];
    } else if ( this.family === 'oklab' && !this.reverse ) {
      // a and b are signed; map [-0.4, 0.4] onto a 0-100% bar so zero sits mid-track.
      const axis = ( v: number ) => Math.max( 0, Math.min( 100, Math.round( ( v + 0.4 ) / 0.8 * 100 ) ) );
      this.channels = [
        { label: 'Light', value: oaL, max: 100, display: oaL + '%', tint: `hsl(0, 0%, ${ oaL }%)`, track: 'rgba(128, 128, 128, 0.16)' },
        { label: 'a', value: axis( oklab[ 1 ] ), max: 100, display: String( oaA ), tint: '#c2410c', track: 'rgba(194, 65, 12, 0.16)' },
        { label: 'b', value: axis( oklab[ 2 ] ), max: 100, display: String( oaB ), tint: '#7c3aed', track: 'rgba(124, 58, 237, 0.16)' },
      ];
    } else if ( this.family === 'lab' && !this.reverse ) {
      // CIELAB a and b are signed and wider; map [-128, 128] onto a 0-100% bar.
      const axis = ( v: number ) => Math.max( 0, Math.min( 100, Math.round( ( v + 128 ) / 256 * 100 ) ) );
      this.channels = [
        { label: 'Light', value: laL, max: 100, display: laL + '%', tint: `hsl(0, 0%, ${ laL }%)`, track: 'rgba(128, 128, 128, 0.16)' },
        { label: 'a', value: axis( lab[ 1 ] ), max: 100, display: String( laA ), tint: '#c2410c', track: 'rgba(194, 65, 12, 0.16)' },
        { label: 'b', value: axis( lab[ 2 ] ), max: 100, display: String( laB ), tint: '#7c3aed', track: 'rgba(124, 58, 237, 0.16)' },
      ];
    } else {
      this.channels = [
        { label: 'Red', value: r, max: 255, display: String( r ), tint: '#d6493f', track: 'rgba(214, 73, 63, 0.16)' },
        { label: 'Green', value: g, max: 255, display: String( g ), tint: '#3aa35e', track: 'rgba(58, 163, 94, 0.16)' },
        { label: 'Blue', value: b, max: 255, display: String( b ), tint: '#3a74e0', track: 'rgba(58, 116, 224, 0.16)' },
      ];
    }
    if ( emit ) { this.colorChange.emit( hex ); }
  }

  private syncChannelTexts() {
    if ( this.decimalFamily ) {
      const f = this.family;
      const v = f === 'p3' ? this.colorService.hexToDisplayP3( this.current )
        : f === 'xyz' ? this.colorService.hexToXyz( this.current ).map( ( n: number ) => Math.round( n * 10000 ) / 100 )
          : f === 'yxy' ? this.colorService.hexToYxy( this.current )
            : f === 'luv' ? this.colorService.hexToLuv( this.current )
              : this.colorService.hexToHunterLab( this.current );
      this.channelTexts = [ String( v[ 0 ] ), String( v[ 1 ] ), String( v[ 2 ] ) ];
      return;
    }
    if ( this.family === 'hsl' ) {
      const hsl = this.colorService.hexToHslInts( this.current );
      this.channelTexts = [ String( hsl.h ), String( hsl.s ), String( hsl.l ) ];
      return;
    }
    if ( this.family === 'hsv' || this.family === 'hsb' ) {
      const hsv = this.colorService.hexToHsv( this.current );
      this.channelTexts = [ String( hsv[ 0 ] ), String( hsv[ 1 ] ), String( hsv[ 2 ] ) ];
      return;
    }
    if ( this.family === 'hwb' ) {
      const hwb = this.colorService.hexToHwb( this.current );
      this.channelTexts = [ String( hwb[ 0 ] ), String( hwb[ 1 ] ), String( hwb[ 2 ] ) ];
      return;
    }
    if ( this.family === 'cmyk' ) {
      const cmyk = this.colorService.hexToCmyk( this.current ).items;
      this.channelTexts = cmyk.map( ( i: { value: number } ) => String( i.value ) );
      return;
    }
    if ( this.family === 'oklch' ) {
      const oklch = this.colorService.hexToOklch( this.current );
      // Integer fields: L%, chroma x1000, hue in degrees.
      this.channelTexts = [ String( Math.round( oklch[ 0 ] * 100 ) ), String( Math.round( oklch[ 1 ] * 1000 ) ), String( Math.round( oklch[ 2 ] ) ) ];
      return;
    }
    if ( this.family === 'lch' ) {
      const lch = this.colorService.hexToLch( this.current );
      // Integer fields: L%, chroma (0..~150), hue in degrees.
      this.channelTexts = [ String( Math.round( lch[ 0 ] ) ), String( Math.round( lch[ 1 ] ) ), String( Math.round( lch[ 2 ] ) ) ];
      return;
    }
    if ( this.family === 'oklab' ) {
      const oklab = this.colorService.hexToOklab( this.current );
      // L as an integer percent; a and b shown as their signed decimals.
      this.channelTexts = [ String( Math.round( oklab[ 0 ] * 100 ) ), String( Math.round( oklab[ 1 ] * 1000 ) / 1000 ), String( Math.round( oklab[ 2 ] * 1000 ) / 1000 ) ];
      return;
    }
    if ( this.family === 'lab' ) {
      const lab = this.colorService.hexToLab( this.current );
      // L integer percent; a and b as signed decimals (~±128).
      this.channelTexts = [ String( Math.round( lab[ 0 ] ) ), String( Math.round( lab[ 1 ] * 100 ) / 100 ), String( Math.round( lab[ 2 ] * 100 ) / 100 ) ];
      return;
    }
    const rgb = this.colorService.hexToRgb( this.current );
    this.channelTexts = [ 0, 1, 2 ].map( i => String( rgb.items[ i ]?.value ?? 0 ) );
  }
}
